"use client";

import { useEffect, useState } from "react";
import { shouldActivatePan } from "./space-pan";

export function useShiftKey(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const sync = (e: KeyboardEvent) => setHeld(e.shiftKey);
    const blur = () => setHeld(false);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("blur", blur);
    };
  }, []);

  return held;
}

export function useAltKey(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const sync = (e: KeyboardEvent | MouseEvent) => setHeld(e.altKey);
    const blur = () => setHeld(false);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("mousemove", sync);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("mousemove", sync);
      window.removeEventListener("blur", blur);
    };
  }, []);

  return held;
}

export function useModKey(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const isMac =
      typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
    const sync = (e: KeyboardEvent | MouseEvent) => {
      setHeld(isMac ? e.metaKey : e.ctrlKey);
    };
    const blur = () => setHeld(false);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("mousemove", sync);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("mousemove", sync);
      window.removeEventListener("blur", blur);
    };
  }, []);

  return held;
}

/** Space pans the canvas unless a focused button or editable field needs the key. */
export function useSpaceKey(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      const active = document.activeElement;
      const focus = active
        ? {
            tagName: active.tagName,
            isContentEditable:
              active instanceof HTMLElement ? active.isContentEditable : false,
          }
        : null;
      if (!shouldActivatePan(e.code, focus)) return;
      e.preventDefault();
      setHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setHeld(false);
    };
    const blur = () => setHeld(false);
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", blur);
    };
  }, []);

  return held;
}
